import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { BookOpen, AlertTriangle, Star, TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface Skill {
  id: number
  skill_id: number
  mastery_level: number
  tier_name: string
  tier_stars: number
  tier_color: string
  total_questions: number
  correct_questions: number
  recent_trend: string | null
  is_weak: boolean
}

interface SkillsData {
  skills: Skill[]
  weak_skills: Skill[]
}

export default function StudentSkills() {
  const { data, isLoading } = useQuery<SkillsData>({
    queryKey: ['student-skills'],
    queryFn: async () => {
      const response = await api.get('/api/student/skills')
      return response.data
    },
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-48 bg-surface-card rounded"></div>
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-32 bg-surface-card rounded-xl"></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-quest-secondary" />
          Skills
        </h1>
        <p className="text-gray-400 mt-1">
          Track your mastery in different areas
        </p>
      </div>

      {/* Weak skills warning */}
      {data?.weak_skills && data.weak_skills.length > 0 && (
        <div className="card border-orange-500/50 bg-orange-900/20">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-orange-500" />
            <h2 className="text-lg font-bold text-orange-300">
              Skills Needing Attention
            </h2>
          </div>
          <div className="grid gap-3">
            {data.weak_skills.map((skill) => (
              <div
                key={skill.id}
                className="flex items-center justify-between p-3 bg-surface-card/50 rounded-lg"
              >
                <div>
                  <p className="font-medium">Skill #{skill.skill_id}</p>
                  <p className="text-sm text-gray-400">
                    {skill.correct_questions}/{skill.total_questions} correct
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-400">
                    {Math.round(skill.mastery_level)}%
                  </p>
                  <p className="text-xs text-gray-500">{skill.tier_name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All skills */}
      <section>
        <h2 className="text-xl font-bold mb-4">All Skills</h2>

        {data?.skills.length === 0 ? (
          <div className="card text-center py-12 text-gray-400">
            <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No skill data yet</p>
            <p className="text-sm mt-1">
              Complete tests and quizzes to track skill mastery
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data?.skills.map((skill) => (
              <SkillCard key={skill.id} skill={skill} />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

function SkillCard({ skill }: { skill: Skill }) {
  const tierColors: Record<string, string> = {
    gray: 'border-gray-500',
    red: 'border-red-500',
    orange: 'border-orange-500',
    blue: 'border-blue-500',
    gold: 'border-yellow-500',
  }

  const TrendIcon = skill.recent_trend === 'improving' ? TrendingUp :
                    skill.recent_trend === 'declining' ? TrendingDown : Minus

  const trendColor = skill.recent_trend === 'improving' ? 'text-green-500' :
                     skill.recent_trend === 'declining' ? 'text-red-500' : 'text-gray-500'

  return (
    <div className={`card border ${tierColors[skill.tier_color] || 'border-gray-700/50'} ${skill.is_weak ? 'bg-red-900/10' : ''}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-medium">Skill #{skill.skill_id}</h3>
          <p className="text-sm text-gray-400">{skill.tier_name}</p>
        </div>
        <div className="flex items-center gap-1">
          {skill.recent_trend && (
            <TrendIcon className={`w-4 h-4 ${trendColor}`} />
          )}
        </div>
      </div>

      {/* Star rating */}
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= skill.tier_stars
                ? 'text-quest-accent fill-quest-accent'
                : 'text-gray-600'
            }`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-400">Mastery</span>
          <span className="font-medium">{Math.round(skill.mastery_level)}%</span>
        </div>
        <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full ${
              skill.is_weak ? 'bg-red-500' :
              skill.mastery_level >= 80 ? 'bg-green-500' :
              skill.mastery_level >= 60 ? 'bg-blue-500' :
              'bg-orange-500'
            }`}
            style={{ width: `${skill.mastery_level}%` }}
          />
        </div>
      </div>

      {/* Stats */}
      <div className="mt-3 text-sm text-gray-400">
        {skill.correct_questions} of {skill.total_questions} questions correct
      </div>
    </div>
  )
}
